import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import AppHeader from '../../components/shell/AppHeader'
import StatusChip from '../../components/agent/StatusChip'
import { supabase } from '../../lib/supabase'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import { fullDate } from '../reports/reportDates'
import { formatTimesInText, useTimeFormat, type TimeFormat } from '../../lib/timeFormat'
import {
  answerSwap,
  getMySwaps,
  getMyWorkShifts,
  getTeammateShifts,
  proposeSwap,
  withdrawSwap,
  type MySwap,
  type ShiftOnDate,
  type SwapResult,
  type TeammateShift,
} from './agentSwapStore'

const fieldClass = 'min-h-11 w-full rounded-control border border-line-strong bg-canvas px-2.5 py-2 text-small'
const labelClass = 'mb-1 block text-caption font-bold text-ink-muted'
const primaryButtonClass = 'flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover'
const secondaryButtonClass = 'flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2'

function ArrowLeftIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  )
}

const shiftText = (shift: ShiftOnDate | TeammateShift, format: TimeFormat) => `${fullDate(shift.date)}, ${formatTimesInText(shift.label, format)}`

// What a swap says to the agent reading it, from where they stand in it.
function statusLine(swap: MySwap): string {
  if (swap.state === 'proposed') return swap.role === 'requester' ? `Waiting for ${swap.otherName} to answer.` : `${swap.otherName} is waiting for your answer.`
  if (swap.state === 'accepted') return 'Agreed. Waiting for your supervisor.'
  if (swap.state === 'declined') return swap.role === 'requester' ? `${swap.otherName} declined.` : 'You declined.'
  return ''
}

function SwapSummary({ swap }: { swap: MySwap }) {
  const { format } = useTimeFormat()
  return (
    <div className="flex flex-col gap-0.5 text-small">
      <p>
        <span className="font-bold">You give: </span>
        {fullDate(swap.giveDate)}, {formatTimesInText(swap.giveShift, format)}
      </p>
      <p>
        <span className="font-bold">You take: </span>
        {fullDate(swap.takeDate)}, {formatTimesInText(swap.takeShift, format)}
      </p>
      {swap.message && <p className="text-ink-muted">&ldquo;{swap.message}&rdquo;</p>}
    </div>
  )
}

/**
 * docs/product/scheduling-model.md's "Shift swaps", from the agent's side: a
 * proposal of a trade with a teammate, the proposals made to the agent, and the
 * state of each swap through the colleague's answer and the supervisor's
 * decision.
 */
export default function AgentSwapsPage() {
  const identity = useProfileIdentity()
  const { format } = useTimeFormat()
  const [userId, setUserId] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [myShifts, setMyShifts] = useState<ShiftOnDate[]>([])
  const [teammateShifts, setTeammateShifts] = useState<TeammateShift[]>([])
  const [swaps, setSwaps] = useState<MySwap[]>([])
  const [result, setResult] = useState<SwapResult | null>(null)

  const [myDate, setMyDate] = useState('')
  const [colleagueId, setColleagueId] = useState('')
  const [theirDate, setTheirDate] = useState('')
  const [message, setMessage] = useState('')

  const load = useCallback(async (id: string) => {
    const [mine, theirs, loadedSwaps] = await Promise.all([getMyWorkShifts(id), getTeammateShifts(id), getMySwaps(id)])
    setMyShifts(mine)
    setTeammateShifts(theirs)
    setSwaps(loadedSwaps)
    setLoaded(true)
  }, [])

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const id = session?.user.id
      if (!id || cancelled) return
      setUserId(id)
      await load(id)
    })
    return () => {
      cancelled = true
    }
  }, [load])

  const myDates = new Set(myShifts.map((shift) => shift.date))
  const colleagues = Array.from(new Map(teammateShifts.map((shift) => [shift.employeeId, shift.name]))).sort((a, b) => a[1].localeCompare(b[1]))
  const colleagueDates = (id: string) => new Set(teammateShifts.filter((shift) => shift.employeeId === id).map((shift) => shift.date))
  // A shift to take is one on a day this agent does not already work, and the
  // colleague does not work the day this agent gives, unless both are the same day.
  const takeable = teammateShifts.filter(
    (shift) => shift.employeeId === colleagueId && (shift.date === myDate || (!myDates.has(shift.date) && !colleagueDates(colleagueId).has(myDate))),
  )

  async function propose(event: FormEvent) {
    event.preventDefault()
    if (!userId) return
    if (!myDate || !colleagueId || !theirDate) {
      setResult({ ok: false, message: 'Choose your shift, a colleague, and the shift you would take.' })
      return
    }
    const made = await proposeSwap(userId, { requesterDate: myDate, counterpartEmployeeId: colleagueId, counterpartDate: theirDate, message })
    setResult(made)
    if (made.ok) {
      setMyDate('')
      setColleagueId('')
      setTheirDate('')
      setMessage('')
      await load(userId)
    }
  }

  async function answer(id: string, accept: boolean) {
    if (!userId) return
    const answered = await answerSwap(userId, id, accept)
    setResult(answered)
    if (answered.ok) await load(userId)
  }

  async function withdraw(id: string) {
    const withdrawn = await withdrawSwap(id)
    setResult(withdrawn)
    if (withdrawn.ok && userId) await load(userId)
  }

  const asked = swaps.filter((swap) => swap.role === 'counterpart' && swap.state === 'proposed')
  const others = swaps.filter((swap) => !asked.includes(swap))

  return (
    <div className="mx-auto flex h-dvh w-full max-w-md flex-col border-line bg-canvas text-ink sm:border-x lg:max-w-none lg:border-x-0">
      <a
        href="#swaps-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="swaps-main" tabIndex={0} className="flex flex-1 flex-col overflow-y-auto">
        <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-6 p-4 lg:p-8">
          <Link to="/agent" className="flex min-h-11 items-center gap-1.5 self-start rounded-control text-small font-bold text-ink-muted">
            <ArrowLeftIcon />
            Dashboard
          </Link>

          <div>
            <h1 className="text-h3 font-extrabold lg:text-h2">Shift Swaps</h1>
            <p className="mt-0.5 text-small text-ink-muted">Trade one of your shifts for a teammate&rsquo;s. They answer first, then your supervisor decides.</p>
          </div>

          {result && !result.ok && (
            <p role="alert" className="text-caption font-semibold text-error">
              {result.message}
            </p>
          )}

          <section aria-labelledby="asked-heading" className="flex flex-col gap-3">
            <h2 id="asked-heading" className="text-h4 font-extrabold">
              Proposals to you
            </h2>
            {!loaded ? (
              <p role="status" className="text-small text-ink-muted">
                Loading&hellip;
              </p>
            ) : asked.length === 0 ? (
              <p className="text-small text-ink-muted">No one has proposed a swap with you.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {asked.map((swap) => (
                  <li key={swap.id} className="rounded-surface border border-line p-3">
                    <p className="text-small font-bold">{swap.otherName} proposed a swap</p>
                    <SwapSummary swap={swap} />
                    <div className="mt-2 flex gap-2">
                      <button type="button" onClick={() => answer(swap.id, true)} className={primaryButtonClass}>
                        Accept
                        <span className="sr-only"> the swap with {swap.otherName}</span>
                      </button>
                      <button type="button" onClick={() => answer(swap.id, false)} className={secondaryButtonClass}>
                        Decline
                        <span className="sr-only"> the swap with {swap.otherName}</span>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="propose-heading" className="flex flex-col gap-3">
            <h2 id="propose-heading" className="text-h4 font-extrabold">
              Propose a swap
            </h2>
            <form onSubmit={propose} aria-label="Propose a swap" className="flex flex-col gap-3 rounded-surface border border-line bg-surface-2 p-3.5">
              <div>
                <label htmlFor="swap-my-shift" className={labelClass}>
                  Your shift to give
                </label>
                <select
                  id="swap-my-shift"
                  value={myDate}
                  onChange={(event) => {
                    setMyDate(event.target.value)
                    setTheirDate('')
                  }}
                  className={fieldClass}
                >
                  <option value="">Choose one of your shifts</option>
                  {myShifts.map((shift) => (
                    <option key={shift.date} value={shift.date}>
                      {shiftText(shift, format)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="swap-colleague" className={labelClass}>
                  Teammate
                </label>
                <select
                  id="swap-colleague"
                  value={colleagueId}
                  onChange={(event) => {
                    setColleagueId(event.target.value)
                    setTheirDate('')
                  }}
                  className={fieldClass}
                >
                  <option value="">Choose a teammate</option>
                  {colleagues.map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="swap-their-shift" className={labelClass}>
                  Their shift to take
                </label>
                <select id="swap-their-shift" value={theirDate} onChange={(event) => setTheirDate(event.target.value)} className={fieldClass}>
                  <option value="">Choose their shift</option>
                  {takeable.map((shift) => (
                    <option key={shift.date} value={shift.date}>
                      {shiftText(shift, format)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="swap-message" className={labelClass}>
                  Message (optional)
                </label>
                <input id="swap-message" type="text" maxLength={500} value={message} onChange={(event) => setMessage(event.target.value)} className={fieldClass} />
              </div>
              <button type="submit" className={`${primaryButtonClass} self-start`}>
                Propose swap
              </button>
            </form>
          </section>

          <section aria-labelledby="swaps-heading" className="flex flex-col gap-3">
            <h2 id="swaps-heading" className="text-h4 font-extrabold">
              Your swaps
            </h2>
            {others.length === 0 ? (
              <p className="text-small text-ink-muted">You have no other swaps.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {others.map((swap) => (
                  <li key={swap.id} className="rounded-surface border border-line p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-small font-bold">With {swap.otherName}</p>
                      {(swap.state === 'approved' || swap.state === 'denied') && <StatusChip state={swap.state} />}
                    </div>
                    <SwapSummary swap={swap} />
                    {statusLine(swap) && <p className="mt-1 text-small font-semibold text-ink-muted">{statusLine(swap)}</p>}
                    {swap.state === 'denied' && swap.decisionComment && (
                      <p className="mt-1 text-small">
                        <span className="font-bold">Denied: </span>
                        {swap.decisionComment}
                      </p>
                    )}
                    {swap.role === 'requester' && (swap.state === 'proposed' || swap.state === 'accepted') && (
                      <button type="button" onClick={() => withdraw(swap.id)} className="mt-1 flex min-h-11 items-center text-small font-bold text-primary">
                        Withdraw
                        <span className="sr-only"> your swap with {swap.otherName}</span>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>
    </div>
  )
}
